import { reaction, makeAutoObservable } from 'mobx';
import {
  GeojsonApi,
  Layer as APILayer,
  MapapiApi,
  LayerLayerTypeEnum,
} from '@datum-group/isogd-openapi/lib';
import {
  DatumLayer,
  HeatmapLayer,
  LayerCollection,
  PhotoLayer,
} from '@datum-map';
import { fromPromise, IPromiseBasedObservable } from 'mobx-utils';
import { LegendItem } from './LegendItem';
import { apiConfig } from '@project/API';

type PromisedLegendItem = IPromiseBasedObservable<LegendItem>;
type Legends = Record<string, PromisedLegendItem>;

export class LegendItemsCollection {
  private _legends: Legends = {};

  constructor(
    private _layersCollection: LayerCollection,
    private _mapAPI = new MapapiApi(apiConfig),
    private _geojsonAPI = new GeojsonApi(apiConfig),
  ) {
    makeAutoObservable(this, undefined, { deep: false });

    reaction(
      () => this._layersCollection.layers,
      () => {
        this._check(this._layersCollection.layers);
      },
      { fireImmediately: true },
    );
  }

  private _check(layers: DatumLayer[]) {
    const newLegends: Legends = {};
    for (let layer of layers) {
      const legendItem = this._legends[layer.name];
      if (legendItem !== undefined) {
        newLegends[layer.name] = legendItem;
        continue;
      }

      newLegends[layer.name] = fromPromise(this._build(layer.name));
    }

    this._legends = newLegends;
  }

  get legends() {
    return Object.keys(this._legends).map(
      (key) => [key, this._legends[key] as PromisedLegendItem] as const,
    );
  }

  private async _build(layerName: string) {
    const datumLayer = this._layersCollection.getLayer(layerName);
    if (datumLayer === undefined) {
      throw new Error('Слой на карте отсутствует!');
    }

    let apiLayer: APILayer;

    if (datumLayer instanceof HeatmapLayer) {
      apiLayer = datumLayer.apiLayer;
    } else if (datumLayer instanceof PhotoLayer) {
      // TODO: На данный момент количество объектов выводится только по одному источнику
      // данных, надо исправить, чтобы выводилось по всем
      apiLayer = {
        data_source: datumLayer.objectManager.source[0]?.id,
        layer_name: datumLayer.name,
        description: datumLayer.description,
        layer_type: LayerLayerTypeEnum.Table,
      };
    } else {
      // todo Когда будет готова вкладка `Слои` - переписать и брать данные из уже загруженных слоев
      const response = await this._mapAPI.apiLayersRead(layerName);
      apiLayer = response.data;
    }

    const size = await this._getLayerSize(apiLayer);

    return new LegendItem(apiLayer, datumLayer, size);
  }

  private async _getLayerSize(layer: APILayer) {
    if (layer.layer_type === 'Table' && !!layer.data_source) {
      const geojsonAPI = this._geojsonAPI;
      const resp = await geojsonAPI.apiGeojsonList(layer.data_source, 1, 1);

      return resp.data.features.length;
    }

    return;
  }
}
